import { API_BASE } from '../config/env';
import cipherServiceInstance from './cipherService';

class StorageService {
  /**
   * Generates a proxy URL for secure file access.
   * This avoids exposing Azure SAS tokens on the frontend.
   *
   * @param originalUrl The original Azure blob URL or relative path
   * @returns A proxy URL pointing to the backend StorageView
   */
  getProxyUrl(originalUrl: string): string {
    if (!originalUrl) return '';

    // If it's already a proxy URL, return as is
    if (originalUrl.includes('/api/v1/storage/')) {
      return originalUrl;
    }

    const encryptedUrl = cipherServiceInstance.encrypt(originalUrl);
    const secretKey = cipherServiceInstance.getSecretKey();

    // Format: /api/v1/storage/<secretKey>/?data=<encryptedPath>
    return `${API_BASE}/api/v1/storage/${secretKey}/?data=${encodeURIComponent(encryptedUrl)}`;
  }

  /**
   * Fetches a proxied file and returns it as a Blob.
   * Useful for internal components that need to render content (e.g. in iframes).
   *
   * @param originalUrl The original Azure blob URL or relative path
   * @returns A Promise resolving to a Blob
   */
  async getFileBlob(originalUrl: string): Promise<Blob> {
    if (!originalUrl) throw new Error('No URL provided');

    const proxyUrl = this.getProxyUrl(originalUrl);

    // Get auth token from cookie (following httpService pattern)
    const getCookie = (name: string): string | null => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
      return null;
    };

    const token = getCookie('auth-token');
    const orgId = localStorage.getItem('current_organization') || 'Empty';

    const response = await fetch(proxyUrl, {
      method: 'GET',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'x-organization-id': orgId,
      },
    });

    if (!response.ok) {
      throw new Error(`Proxy fetch failed: ${response.status}`);
    }

    return await response.blob();
  }

  /**
   * Opens a proxied file using an authorized fetch and Blob URL.
   * This respects production security headers and authentication rules.
   *
   * @param originalUrl The original Azure blob URL or relative path
   */
  async viewFile(originalUrl: string): Promise<void> {
    try {
      const blob = await this.getFileBlob(originalUrl);
      const blobUrl = URL.createObjectURL(blob);

      // Open the Blob URL in a new tab
      window.open(blobUrl, '_blank');

      // Cleanup blob URL after 1 minute
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      console.error('Secure file viewing failed, falling back to direct proxy navigation:', error);
      const proxyUrl = this.getProxyUrl(originalUrl);
      window.open(proxyUrl, '_blank');
    }
  }
}

const storageService = new StorageService();
export default storageService;
